import type { EventResponse } from '@@/types/api';

/**
 * Strip Markdown syntax to plain text suitable for HTML attribute values
 * (meta tag content, og:description, etc.). Coarse-grained — covers the
 * common cases that show up in event descriptions and produces output that
 * social-media scrapers can parse without choking on literal newlines or
 * Markdown punctuation.
 */
export function stripMarkdownToPlain(input: string): string {
  let out = input;

  // 1. Fenced code blocks — strip fences AND inner content (alt syntax: ~~~ also handled)
  out = out.replace(/\r?\n?```[\s\S]*?```\r?\n?/g, ' ');
  out = out.replace(/\r?\n?~~~[\s\S]*?~~~\r?\n?/g, ' ');

  // 2. Images — drop entirely (alt text is rarely useful in a 200-char preview)
  out = out.replace(/!\[[^\]]*\]\([^)]*\)/g, '');

  // 3. Links — keep visible text, drop URL
  out = out.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');

  // 4. ATX headings — strip leading #+\s+
  out = out.replace(/^[ \t]*#+[ \t]+/gm, '');

  // 5. Setext heading underlines — drop a line consisting of only = or - directly below a non-blank line
  out = out.replace(/^(?!\s*$)(.+)\r?\n[ \t]*(=+|-+)[ \t]*$/gm, '$1');

  // 6. Blockquote markers
  out = out.replace(/^[ \t]*>[ \t]?/gm, '');

  // 7. List markers (unordered + ordered)
  out = out.replace(/^[ \t]*[*+-][ \t]+/gm, '');
  out = out.replace(/^[ \t]*\d+\.[ \t]+/gm, '');

  // 8. Horizontal rules — lines made only of -, *, or _ (3+ chars, optional whitespace between)
  out = out.replace(/^[ \t]*([-*_])(?:[ \t]*\1){2,}[ \t]*$/gm, '');

  // 9. Inline code
  out = out.replace(/`([^`]*)`/g, '$1');

  // 10. Bold/italic — process triple, then double, then single (asterisks first, then underscores)
  out = out.replace(/\*\*\*([^*]+)\*\*\*/g, '$1');
  out = out.replace(/___([^_]+)___/g, '$1');
  out = out.replace(/\*\*([^*]+)\*\*/g, '$1');
  out = out.replace(/__([^_]+)__/g, '$1');
  out = out.replace(/\*([^*]+)\*/g, '$1');
  out = out.replace(/_([^_]+)_/g, '$1');

  // 11. Leaked HTML tags
  out = out.replace(/<[^>]+>/g, '');

  // 12. Collapse whitespace
  out = out.replace(/\s+/g, ' ');

  // 13. Trim
  return out.trim();
}

/**
 * Truncate at the last word boundary that fits within `max` characters,
 * appending an ellipsis when truncation occurs. The returned string is
 * always at most `max` characters long.
 */
export function truncateAtWord(input: string, max: number): string {
  if (input.length <= max) return input;

  const window = input.slice(0, max - 1);
  const lastSpace = window.search(/\s\S*$/);

  if (lastSpace <= 0) {
    return `${input.slice(0, max - 1)}…`;
  }

  return `${window.slice(0, lastSpace).trimEnd()}…`;
}

/**
 * Build the description string used in <meta name="description"> and
 * <meta property="og:description"> for an event. Prefers the
 * organizer-authored short_description when present; otherwise falls back
 * to a stripped-Markdown plain-text rendering of the long-form description.
 * Result is always ≤ 200 characters and contains no literal newlines or
 * Markdown punctuation.
 */
export function buildPageDescription(
  event: Pick<EventResponse, 'short_description' | 'description'>,
): string {
  const raw =
    event.short_description?.trim() ||
    stripMarkdownToPlain(event.description ?? '');
  return truncateAtWord(raw, 200);
}
